import { useEffect, useState } from 'react';
import { collection, getDocs, limit, orderBy, query, where } from 'firebase/firestore';
import { db } from '../../firebase';

/**
 * DREW's headline status for the REPSit dashboard widget (P3-20 follow-up, Alex 09-12: "the
 * main function of DREW — the status of drift and accumulation — as a widget; click opens DREW").
 *
 * Two one-shot reads per organization (no listeners; cached in memory for STATUS_TTL_MS so
 * re-renders, the gallery preview and a second instance cost nothing):
 *   - the latest organization-level OE snapshot → REPS scores, element scores, warnings
 *   - open OE Conditions (`Hugo_TRLs`, not resolved/rejected/withdrawn/cancelled) → counts by type
 *
 * Returns { loading, error, reps, drift, accumulation, warnings, open: { total, drift, trap,
 * roadblock, landmine }, calculatedAt }.
 */
export const STATUS_TTL_MS = 5 * 60 * 1000;
const cache = new Map(); // orgId → { at, data }
const CLOSED = new Set(['resolved', 'rejected', 'withdrawn', 'cancelled']);

function avg(values) {
  const nums = values.filter((v) => typeof v === 'number' && !Number.isNaN(v));
  return nums.length ? nums.reduce((a, b) => a + b, 0) / nums.length : null;
}

/** An OE element score is `{ R, E, P, S }` (scoring.calculateElementBreakdown) → its mean. */
function elementAvg(el) {
  if (!el) return null;
  if (typeof el === 'number') return el;
  return avg(['R', 'E', 'P', 'S'].map((k) => el[k]));
}

export async function fetchDrewStatus(orgId) {
  const snapQ = query(
    collection(db, 'Hugo_OESnapshots'),
    where('organizationId', '==', orgId),
    where('scope.type', '==', 'organization'),
    orderBy('calculatedAt', 'desc'),
    limit(1)
  );
  const trlQ = query(collection(db, 'Hugo_TRLs'), where('organizationId', '==', orgId), orderBy('submittedAt', 'desc'), limit(500));
  const [snapRes, trlRes] = await Promise.allSettled([getDocs(snapQ), getDocs(trlQ)]);

  if (snapRes.status === 'rejected') console.warn('DREW status widget: snapshot query failed', snapRes.reason?.message || snapRes.reason);
  if (trlRes.status === 'rejected') console.warn('DREW status widget: OE Conditions query failed', trlRes.reason?.message || trlRes.reason);
  const latest = snapRes.status === 'fulfilled' && !snapRes.value.empty ? snapRes.value.docs[0].data() : null;
  const el = latest?.elementScores || {};
  const open = { total: 0, drift: 0, trap: 0, roadblock: 0, landmine: 0 };
  if (trlRes.status === 'fulfilled') {
    trlRes.value.forEach((d) => {
      const t = d.data();
      if (CLOSED.has(t.status)) return;
      open.total += 1;
      const type = String(t.type || t.trlType || '').toLowerCase();
      if (type in open) open[type] += 1;
    });
  }
  return {
    reps: latest?.oeScores || null,
    drift: avg([elementAvg(el.driftPerception), elementAvg(el.driftWork)]),
    accumulation: avg([elementAvg(el.traps), elementAvg(el.roadblocks), elementAvg(el.landmines)]),
    warnings: Array.isArray(latest?.warnings) ? latest.warnings : [],
    calculatedAt: latest?.calculatedAt || null,
    open,
    hasSnapshot: Boolean(latest),
    trlError: trlRes.status === 'rejected' ? trlRes.reason : null,
  };
}

export function useDrewStatus(orgId) {
  const hit = orgId ? cache.get(orgId) : null;
  const fresh = hit && Date.now() - hit.at < STATUS_TTL_MS;
  const [state, setState] = useState(() => ({ loading: Boolean(orgId) && !fresh, error: null, data: fresh ? hit.data : null }));

  useEffect(() => {
    let cancelled = false;
    if (!orgId) {
      setState({ loading: false, error: null, data: null });
      return undefined;
    }
    const cached = cache.get(orgId);
    if (cached && Date.now() - cached.at < STATUS_TTL_MS) {
      setState({ loading: false, error: null, data: cached.data });
      return undefined;
    }
    setState((s) => ({ ...s, loading: true, error: null }));
    fetchDrewStatus(orgId)
      .then((data) => {
        cache.set(orgId, { at: Date.now(), data });
        if (!cancelled) setState({ loading: false, error: null, data });
      })
      .catch((error) => {
        console.warn('DREW status widget:', error?.message || error);
        if (!cancelled) setState({ loading: false, error, data: null });
      });
    return () => {
      cancelled = true;
    };
  }, [orgId]);

  return { loading: state.loading, error: state.error, ...(state.data || {}) };
}

export default useDrewStatus;
